import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import { FlagFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import { serverFetch } from '@server';
import { showError, fmtDate, fmtDateApi } from '@extensions/utils';
import { useSelectCompany } from '@extensions/company';
import TaskActivity from './task-activity-graph';
import { TASK_PRIORITIES } from './task-priority';
import Task from './task';
import WeekPlan from './week-plan';
import Contragent from '../contragents/contragent';
import { dueState, ratedOpen } from '../pulse/pulse-utils';
import './dashboard.css';

const LIMIT = 8;

export const QUADRANTS = [
    { priority: 1, importance: 'yes', urgency: 'yes', color: '#ff4d4f' },
    { priority: 2, importance: 'yes', urgency: 'no', color: '#1677ff' },
    { priority: 3, importance: 'no', urgency: 'yes', color: '#fa8c16' },
    { priority: 4, importance: 'no', urgency: 'no', color: '#8c8c8c' },
];

export const byPlanDate = (a, b) => {
    if (!a.planDate && !b.planDate) return b.id - a.id;
    if (!a.planDate) return 1;
    if (!b.planDate) return -1;
    return String(a.planDate).localeCompare(String(b.planDate)) || (b.id - a.id);
};

const Dashboard = () => {
    const navigate = useNavigate();
    const user = useSelector((s) => s.user);
    const userId = user?.userId || 0;
    const selectCompany = useSelectCompany();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activity, setActivity] = useState([]);
    const [priority, setPriority] = useState(1);
    const [openTasks, setOpenTasks] = useState([]);
    const [editTask, setEditTask] = useState(null);
    const [editContragent, setEditContragent] = useState(null);
    const [planKey, setPlanKey] = useState(0);

    const today = fmtDateApi(dayjs());
    const mine = userId ? `&userId=${userId}` : '';
    const journalUser = userId ? `&user=${userId}` : '';

    const load = useCallback(() => {
        if (!userId) return;
        serverFetch(`task/activity?userId=${userId}`).then(setActivity).catch(showError);
        serverFetch(`task?vid=3${mine}`)
            .then((items) => setOpenTasks(ratedOpen(items)))
            .catch(showError);
    }, [userId, mine]);

    useEffect(() => { load(); }, [load]);

    // ?task=&tc= — задача из плана, открытая после переключения фирмы
    useEffect(() => {
        const id = Number(searchParams.get('task'));
        if (!id) return;
        if (Number(searchParams.get('tc')) === user?.companyId) setEditTask(id);
        setSearchParams({}, { replace: true });
    }, [searchParams, setSearchParams, user?.companyId]);

    const openPlanTask = (task) => {
        if (task.companyId === user?.companyId) setEditTask(task.id);
        else selectCompany({ id: task.companyId, name: task.companyName }, `/?task=${task.id}&tc=${task.companyId}`);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const todayStart = useMemo(() => dayjs().startOf('day'), [today]);
    const todayTasks = useMemo(
        () => openTasks.filter((task) => dueState(task, todayStart) === 'today').sort(byPlanDate),
        [openTasks, todayStart],
    );
    const totals = useMemo(() => ({
        qtyOpen: openTasks.length,
        qtyToday: todayTasks.length,
        qtyOld: openTasks.filter((task) => dueState(task, todayStart) === 'overdue').length,
    }), [openTasks, todayTasks, todayStart]);

    const priorityTasks = useMemo(
        () => openTasks.filter((task) => task.priority === priority).sort(byPlanDate),
        [openTasks, priority],
    );
    const priorityCount = (value) => openTasks.filter((task) => task.priority === value).length;

    const openDay = (cell) => navigate(`/tasks?vid=2&p1=${cell.key}&p2=${cell.key}${journalUser}`);
    const quadrant = QUADRANTS.find((item) => item.priority === priority) ?? QUADRANTS[0];

    return (
        <div className="dashboard">
            <h2 className="dashboard-title">Главная</h2>
            <div className="dashboard-stats">
                <button type="button" className="dashboard-stat" onClick={() => navigate(`/tasks?vid=3${journalUser}`)}>
                    <span className="dashboard-stat-value" style={{ color: '#3f8f10' }}>{totals.qtyOpen}</span>
                    <span className="dashboard-stat-label">Открытых задач</span>
                </button>
                <button type="button" className="dashboard-stat" onClick={() => navigate(`/tasks?vid=2&p1=${today}&p2=${today}${journalUser}`)}>
                    <span className="dashboard-stat-value" style={{ color: '#fa8c16' }}>{totals.qtyToday}</span>
                    <span className="dashboard-stat-label">На сегодня</span>
                </button>
                <button type="button" className="dashboard-stat" onClick={() => navigate(`/tasks?vid=4${journalUser}`)}>
                    <span className="dashboard-stat-value" style={{ color: '#ff4d4f' }}>{totals.qtyOld}</span>
                    <span className="dashboard-stat-label">Просрочено</span>
                </button>
            </div>
            <section className="dashboard-card">
                <TaskActivity days={activity} onDayClick={openDay} />
            </section>
            <WeekPlan onOpen={openPlanTask} reloadKey={planKey} allCompanies={!!user?.isSuperadmin} />
            <div className="dashboard-split">
                <TaskColumn
                    title="Срок сегодня"
                    items={todayTasks}
                    onOpen={setEditTask}
                    onAll={() => navigate(`/tasks?vid=2&p1=${today}&p2=${today}${journalUser}`)}
                />
                <section className="dashboard-card">
                    <div className="dashboard-card-head">
                        <h3>Приоритет</h3>
                        {priorityTasks.length > 0 && (
                            <button
                                type="button"
                                className="dashboard-all"
                                onClick={() => navigate(`/tasks?vid=3&importance=${quadrant.importance}&urgency=${quadrant.urgency}${journalUser}`)}
                            >
                                {priorityTasks.length > LIMIT ? `Все ${priorityTasks.length}` : 'Все'}
                            </button>
                        )}
                    </div>
                    <div className="dashboard-flags">
                        {QUADRANTS.map((item) => {
                            const label = TASK_PRIORITIES.find((p) => p.value === item.priority)?.label;
                            const active = item.priority === priority;
                            const count = priorityCount(item.priority);
                            return (
                                <Tooltip key={item.priority} title={label}>
                                    <button
                                        type="button"
                                        className={`dashboard-flag${active ? ' active' : ''}`}
                                        style={{ '--cell-color': item.color }}
                                        aria-label={`${label}: ${count}`}
                                        onClick={() => setPriority(item.priority)}
                                    >
                                        <FlagFilled />
                                        <span className="dashboard-flag-count">{count}</span>
                                    </button>
                                </Tooltip>
                            );
                        })}
                    </div>
                    {priorityTasks.length === 0 && <div className="dashboard-empty">Нет задач</div>}
                    {priorityTasks.slice(0, LIMIT).map((task) => (
                        <button key={task.id} type="button" className="dashboard-task" onClick={() => setEditTask(task.id)}>
                            <span className="dashboard-task-name">{task.name}</span>
                            <span className={`dashboard-task-meta${task.planDate && dayjs(task.planDate).isBefore(todayStart) ? ' overdue' : ''}`}>
                                {fmtDate(task.planDate)}
                                {task.userName ? ` · ${task.userName}` : ''}
                            </span>
                        </button>
                    ))}
                </section>
            </div>
            {editTask !== null && (
                <Task
                    id={editTask}
                    onClose={() => { setEditTask(null); load(); setPlanKey((k) => k + 1); }}
                    onOpenContragent={setEditContragent}
                />
            )}
            {editContragent !== null && (
                <Contragent id={editContragent} onClose={() => setEditContragent(null)} />
            )}
        </div>
    );
};

const TaskColumn = ({ title, items, overdue, onOpen, onAll }) => (
    <section className="dashboard-card">
        <div className="dashboard-card-head">
            <h3>{title}</h3>
            {items.length > 0 && (
                <button type="button" className="dashboard-all" onClick={onAll}>
                    {items.length > LIMIT ? `Все ${items.length}` : 'Все'}
                </button>
            )}
        </div>
        {items.length === 0 && <div className="dashboard-empty">Нет задач</div>}
        {items.slice(0, LIMIT).map((task) => (
            <button key={task.id} type="button" className="dashboard-task" onClick={() => onOpen(task.id)}>
                <span className="dashboard-task-name">{task.name}</span>
                <span className={`dashboard-task-meta${overdue ? ' overdue' : ''}`}>
                    {fmtDate(task.planDate)}
                    {task.userName ? ` · ${task.userName}` : ''}
                </span>
            </button>
        ))}
    </section>
);

export default Dashboard;
